import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const formatDate = (dateValue) => {
  if (!dateValue) return 'Date TBA';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 'Date TBA';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PreviousRecordedGames = ({
  matchType = 'league',
  games = [],
  loading = false,
  error = null,
  selectedGameId = '',
  onSelectGame,
  onRetry,
}) => {
  const typeLabel = matchType === 'tournament' ? 'tournament' : 'league';

  return (
    <div className="bg-card border border-border rounded-lg card-shadow">
      <div className="p-4 border-b border-border flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Previous Recorded Games</h2>
          <p className="text-sm text-muted-foreground">
            Completed {typeLabel} games with recorded scores and stats
          </p>
        </div>
        <Icon name="History" size={20} className="text-muted-foreground shrink-0" />
      </div>

      <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
        {loading && (
          <div className="py-10 text-center text-muted-foreground space-y-2">
            <Icon name="Loader2" size={28} className="mx-auto animate-spin text-primary" />
            <p className="text-sm">Loading previous {typeLabel} results...</p>
          </div>
        )}

        {!loading && error && (
          <div className="py-8 text-center text-destructive space-y-2 px-4">
            <Icon name="AlertTriangle" size={24} className="mx-auto" />
            <p className="text-sm">{error}</p>
            {onRetry && (
              <Button variant="outline" size="sm" iconName="RefreshCcw" onClick={onRetry}>
                Retry
              </Button>
            )}
          </div>
        )}

        {!loading && !error && games.length === 0 && (
          <div className="py-10 text-center text-muted-foreground space-y-2 px-4">
            <Icon name="ClipboardList" size={28} className="mx-auto" />
            <p className="text-sm">
              No completed {typeLabel} games with recorded stats yet.
            </p>
          </div>
        )}

        {!loading &&
          !error &&
          games.map((game) => {
            const isSelected = String(game.id) === String(selectedGameId);
            const home =
              matchType === 'tournament'
                ? game.homeTeam?.teamName || 'Home'
                : game.homeTeam?.name || game.homeTeam?.shortName || 'Home';
            const away =
              matchType === 'tournament'
                ? game.awayTeam?.teamName || 'Away'
                : game.awayTeam?.name || game.awayTeam?.shortName || 'Away';
            const homeScore = game.homeScore ?? 0;
            const awayScore = game.awayScore ?? 0;
            const meta =
              matchType === 'tournament'
                ? game.stageName || 'Tournament'
                : game.league?.name
                  ? `${game.league.name}${game.league.season ? ` · ${game.league.season}` : ''}`
                  : 'League';

            return (
              <div
                key={game.id}
                className={`p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
                  isSelected ? 'bg-primary/5' : ''
                }`}
              >
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide text-primary font-semibold">
                    {meta}
                  </p>
                  <p className="font-medium text-foreground truncate">
                    {home} vs {away}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(game.scheduledDate || game.endedAt)}
                    {game.venue ? ` · ${game.venue}` : ''}
                  </p>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <p className="text-lg font-bold text-foreground tabular-nums">
                      {homeScore} – {awayScore}
                    </p>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-success/10 text-success">
                      Completed
                    </span>
                  </div>
                  {onSelectGame && (
                    <Button
                      variant={isSelected ? 'default' : 'outline'}
                      size="sm"
                      iconName="BarChart3"
                      onClick={() => onSelectGame(game)}
                    >
                      {isSelected ? 'Viewing' : 'View Stats'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default PreviousRecordedGames;
